import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Cloud, 
  FileText, 
  CheckCircle2, 
  Activity, 
  Globe, 
  Database, 
  Eye, 
  RefreshCw,
  Clock,
  Trash2,
  LineChart,
  AreaChart,
  BarChart2
} from 'lucide-react';
import Chart from 'react-apexcharts';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import api from '../api/axios';

import { SensorConfig } from './NewSensorModal';

interface ExistingDataset {
  id: string;
  label: string;
  metadata?: any;
}

interface AddDataResourceModalProps {
  projectId: string;
  sensorConfig: SensorConfig | null;
  existingDataset?: ExistingDataset | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (result?: { added?: number; append?: boolean }) => void;
}

type SourceType = 'csv' | 'xlsx' | 'api' | '';

interface FileData {
  name: string;
  columns: string[];
  data: any[];
}

interface FormData {
  sourceType: SourceType;
  file?: FileData;
  apiEndpoint: string;
  // Fonte "API" é sempre tratada como sensor em tempo real: em vez de um pedido
  // único, a dashboard faz polling a este URL a cada "pollIntervalSec" segundos
  // (ver MonitoringDashboard.tsx e mqtt-handler/wilow_realtime_bridge.py).
  pollIntervalSec: number;
  timeColumn: string;
  selectedColumns: string[];
}

interface PreviewState {
  isProcessing: boolean;
  isComplete: boolean;
  entryCount: number;
  data: any[];
  error?: string;
}

const parseDate = (rawDate: any): string => {
  if (!rawDate) return new Date().toISOString();
  if (rawDate instanceof Date) return rawDate.toISOString();
  
  const dateStr = String(rawDate).trim();
  
  // Attempt to parse DD/MM/YY HH:mm or similar
  if (dateStr.includes('/')) {
    const parts = dateStr.split(' ');
    const dateParts = parts[0].split('/');
    if (dateParts.length === 3) {
      const timeParts = parts[1] ? parts[1].split(':') : ['00', '00'];
      let day = parseInt(dateParts[0]);
      let month = parseInt(dateParts[1]) - 1;
      let year = parseInt(dateParts[2]);
      if (year < 100) year += 2000;
      
      const dateObj = new Date(year, month, day, parseInt(timeParts[0]), parseInt(timeParts[1]));
      if (!isNaN(dateObj.getTime())) return dateObj.toISOString();
    }
  }
  
  const fallback = new Date(dateStr);
  return !isNaN(fallback.getTime()) ? fallback.toISOString() : dateStr;
};

const AddDataResourceModal: React.FC<AddDataResourceModalProps> = ({
  projectId,
  sensorConfig,
  existingDataset,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const isAppend = !!existingDataset;
  const sensorLabel = sensorConfig?.sensorName || existingDataset?.label || 'Novo Sensor';
  // Em append, a fonte tem de ser do mesmo tipo do sensor original (API=realtime; ficheiro=estático).
  const originalIsApi = isAppend && (
    existingDataset?.metadata?.sourceType === 'api' ||
    existingDataset?.metadata?.sourceType === 'realtime' ||
    String(existingDataset?.metadata?.dataSource || '').startsWith('http')
  );
  const allowedSources = !isAppend ? ['csv', 'xlsx', 'api'] : (originalIsApi ? ['api'] : ['csv', 'xlsx']);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    sourceType: '',
    file: undefined,
    apiEndpoint: '',
    pollIntervalSec: 1,
    timeColumn: '',
    selectedColumns: [],
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessingColumns, setIsProcessingColumns] = useState(false);
  const [sensorMetadata, setSensorMetadata] = useState<any>(null);
  const [preview, setPreview] = useState<PreviewState>({
    isProcessing: false,
    isComplete: false,
    entryCount: 0,
    data: [],
  });
  const [showDataPreview, setShowDataPreview] = useState(false);
  const [previewChartType, setPreviewChartType] = useState<string>('area');
  // Última leitura recebida ao testar a ligação da API (fonte "API" = sempre tempo real).
  const [apiPreviewChannels, setApiPreviewChannels] = useState<{ [key: string]: number }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && sensorConfig) {
      setSensorMetadata({ ...sensorConfig });
    } else if (isOpen && existingDataset) {
      // Modo append: herda os metadados do sensor existente (tipos de gráfico, etc.).
      setSensorMetadata({ ...(existingDataset.metadata || {}) });
      // Se só há uma fonte possível (API), pré-seleciona-a.
      if (allowedSources.length === 1) {
        setFormData(prev => ({ ...prev, sourceType: allowedSources[0] as SourceType }));
      }
    }
    if (!isOpen) {
      setStep(1);
      setFormData({
        sourceType: '',
        file: undefined,
        apiEndpoint: '',
        pollIntervalSec: 1,
        timeColumn: '',
        selectedColumns: [],
      });
      setErrors({});
      setPreview({ isProcessing: false, isComplete: false, entryCount: 0, data: [] });
      setShowDataPreview(false);
      setIsUploading(false);
      setIsProcessingColumns(false);
      setSensorMetadata(null);
      setApiPreviewChannels({});
    }
  }, [isOpen, sensorConfig, existingDataset]);

  const validateStep = () => {
    const newErrors: { [key: string]: string } = {};

    if (step === 1) {
      if (!formData.sourceType) newErrors.sourceType = 'Tipo de fonte é obrigatório.';
    } else if (step === 2) {
      if (formData.sourceType === 'csv' || formData.sourceType === 'xlsx') {
        if (!formData.file) newErrors.file = 'Carregue um ficheiro para continuar.';
      } else if (formData.sourceType === 'api') {
        if (!formData.apiEndpoint.trim()) newErrors.apiEndpoint = 'URL do endpoint é obrigatória.';
        if (!formData.apiEndpoint.startsWith('http')) newErrors.apiEndpoint = 'URL deve começar com http:// ou https://';
      }
    } else if (step === 3 && (formData.sourceType === 'csv' || formData.sourceType === 'xlsx')) {
      if (!formData.timeColumn) newErrors.timeColumn = 'Selecione a coluna de tempo.';
      if (formData.selectedColumns.length === 0) newErrors.selectedColumns = 'Selecione pelo menos uma coluna para importar.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep()) return;

    if (step === 2 && formData.sourceType === 'api') {
      await startProcessing();
      setStep(4);
    } else if (step === 3) {
      setIsProcessingColumns(true);
      await startProcessing();
      setIsProcessingColumns(false);
      setStep(4);
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step === 4 && formData.sourceType === 'api') {
      setStep(2);
    } else {
      setStep(step - 1);
    }
  };

  const chartTypeMap: { [key: string]: string } = {
    'Linha': 'line',
    'Área': 'area',
    'Barras': 'bar',
    'Scatter': 'scatter'
  };

  const reverseChartTypeMap: { [key: string]: string } = {
    'line': 'Linha',
    'area': 'Área',
    'bar': 'Barras',
    'scatter': 'Scatter'
  };

  const handleUpdateDefaultChartType = async (type: string) => {
    const portugueseType = reverseChartTypeMap[type];
    setPreviewChartType(type);
    if (sensorMetadata) {
      setSensorMetadata({ ...sensorMetadata, defaultChartType: portugueseType });
    }
  };

  const startProcessing = async () => {
    setPreview({ ...preview, isProcessing: true, isComplete: false, error: undefined });

    if (formData.sourceType === 'api') {
      // Fonte "API" é sempre tempo real: testa mesmo a ligação (sem atraso artificial -
      // o URL é do utilizador, pode estar em qualquer rede, e queremos falhar depressa).
      // O teste corre no servidor (auth-service), não no browser - o browser já não fala
      // HTTP diretamente com o mqtt-handler, ver a nota em CLAUDE.md sobre o proxy de tempo real.
      try {
        const { data: body } = await api.post(`/projects/${projectId}/realtime/test`, { url: formData.apiEndpoint.trim() });
        const channels = body.channels || {};
        setApiPreviewChannels(channels);
        setPreview({
          isProcessing: false,
          isComplete: true,
          entryCount: Object.keys(channels).length,
          data: [],
        });
      } catch (err) {
        setApiPreviewChannels({});
        setPreview({
          ...preview,
          isProcessing: false,
          isComplete: false,
          error: 'Não foi possível ligar à API. Confirma o URL, que o serviço está acessível a partir daqui e que o protocolo (http/https) coincide com o da dashboard.',
        });
      }
      return;
    }

    // Simulate processing time as per requirement ("A gerar pré-visualização...") - só ficheiros.
    await new Promise(resolve => setTimeout(resolve, 2500));

    // Apply parsing to preview data
    const sourceData = (formData.file?.data || []).map(row => {
      const entry: any = { ...row };
      entry[formData.timeColumn] = parseDate(row[formData.timeColumn]);
      return entry;
    });

    setPreview({
      isProcessing: false,
      isComplete: true,
      entryCount: sourceData.length,
      data: sourceData,
    });
    if (sensorMetadata?.defaultChartType) {
      setPreviewChartType(chartTypeMap[sensorMetadata.defaultChartType as string] || 'area');
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (formData.sourceType !== ext) {
      setErrors({ file: `Por favor, carregue um ficheiro .${formData.sourceType.toUpperCase()}` });
      return;
    }

    setIsUploading(true);
    setErrors({});

    const reader = new FileReader();
    reader.onload = (event) => {
      setTimeout(() => {
        try {
          let columns: string[] = [];
          let data: any[] = [];

          if (formData.sourceType === 'csv') {
            const result = Papa.parse(event.target?.result as string, { header: true, skipEmptyLines: true });
            data = result.data;
            columns = data.length > 0 ? Object.keys(data[0]) : [];
          } else {
            const workbook = XLSX.read(event.target?.result, { type: 'binary' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            data = XLSX.utils.sheet_to_json(firstSheet);
            columns = data.length > 0 ? Object.keys(data[0]) : [];
          }

          const timeCol = columns.find(c => 
            ['date', 'data', 'timestamp', 'tempo', 'time'].includes(c.toLowerCase())
          ) || columns[0] || '';

          setFormData({
            ...formData,
            file: { name: file.name, columns, data },
            timeColumn: timeCol,
            selectedColumns: columns.filter(c => c !== timeCol)
          });
          setErrors({});
        } catch (err) {
          setErrors({ file: 'Erro ao processar o ficheiro.' });
        } finally {
          setIsUploading(false);
        }
      }, 1500);
    };

    if (formData.sourceType === 'csv') {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  };

  const handleSubmit = async () => {
    if (!sensorConfig && !existingDataset) return;
    setIsSubmitting(true);
    try {
      const isApi = formData.sourceType === 'api';

      // Fonte "API": sem dados históricos para enviar - o sensor é sempre tempo real,
      // a dashboard é que faz o polling ao URL (ver MonitoringDashboard.tsx). As colunas
      // ficam a cargo da 1.ª leitura recebida quando não há canais detetados no teste.
      const mappedData = isApi ? [] : (formData.file?.data || []).map(row => {
        const entry: any = { date: parseDate(row[formData.timeColumn]) };
        formData.selectedColumns.forEach(col => {
          const val = parseFloat(String(row[col]).replace(',', '.'));
          entry[col] = isNaN(val) ? null : val;
        });
        return entry;
      });

      const { data: res } = await api.patch(`/projects/${projectId}/data`, {
        id: existingDataset?.id,
        label: sensorLabel,
        mode: isAppend ? 'append' : undefined,
        metadata: {
          ...sensorMetadata,
          sourceType: isApi ? 'realtime' : formData.sourceType,
          dataSource: isApi ? formData.apiEndpoint.trim() : formData.file?.name,
          ...(isApi ? { pollIntervalMs: Math.max(100, Math.round(formData.pollIntervalSec * 1000)) } : {}),
          timeColumn: formData.timeColumn,
          selectedColumns: isApi ? Object.keys(apiPreviewChannels) : formData.selectedColumns,
        },
        data: mappedData
      });
      onSuccess({ added: res?.added, append: isAppend });
      onClose();
    } catch (err) {
      setErrors({ submit: 'Erro ao guardar o recurso de dados. Tente novamente.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const steps = [
    { id: 1, label: 'Tipo de Fonte' },
    { id: 2, label: 'Configuração' },
    ...(formData.sourceType !== 'api' ? [{ id: 3, label: 'Colunas' }] : []),
    { id: 4, label: 'Pré-visualização' }
  ];

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-[#05070a]/90 backdrop-blur-sm transition-all animate-in fade-in duration-300">
      <div className="bg-white dark:bg-[#161b26] rounded-[3rem] border border-slate-200 dark:border-slate-800 max-w-2xl w-full overflow-hidden shadow-2xl animate-in zoom-in duration-300 transition-colors">
        
        {/* Progress Header */}
        <div className="p-8 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">{isAppend ? 'Adicionar Dados' : 'Adicionar Recurso'}</h3>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors">
              <X size={20} className="text-slate-400" />
            </button>
          </div>
          
          <div className="flex items-center justify-center space-x-4">
            {steps.map((s, idx) => (
              <div key={s.id} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${step >= s.id ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-200 dark:border-slate-700 text-slate-300'}`}>
                  {step > s.id ? <CheckCircle2 size={20} /> : <span className="font-black text-xs">{idx + 1}</span>}
                </div>
                {idx < steps.length - 1 && (
                  <div className={`w-12 h-0.5 mx-2 ${step > steps[idx+1].id ? 'bg-blue-600' : 'bg-slate-200 dark:bg-slate-700'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-4 px-2">
             {steps.map(s => (
               <span key={s.id} className={`text-[9px] font-black uppercase tracking-widest ${step === s.id ? 'text-blue-600' : 'text-slate-400'}`}>{s.label}</span>
             ))}
          </div>
        </div>

        <div className="p-10 max-h-[60vh] overflow-y-auto custom-scrollbar">
          {/* Read-only sensor info */}
          <div className="mb-8 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center gap-3">
            <div className="p-2 bg-blue-600/10 text-blue-600 rounded-lg">
              <Activity size={16} />
            </div>
            <div>
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{isAppend ? 'A adicionar dados a' : 'Sensor Selecionado'}</p>
              <p className="text-sm font-black text-slate-900 dark:text-white uppercase">{sensorLabel}</p>
              {isAppend && <p className="text-[9px] font-bold text-amber-500 uppercase tracking-widest mt-0.5">Só serão considerados registos posteriores ao último dado existente</p>}
            </div>
          </div>

          {step === 1 && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              <div>
                <h4 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Tipo de Fonte</h4>
                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-1">Escolha a origem dos dados</p>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {[
                  { id: 'csv', label: 'Ficheiro CSV', icon: FileText },
                  { id: 'xlsx', label: 'Ficheiro XLSX', icon: Database },
                  { id: 'api', label: 'API', icon: Globe }
                ].filter((t) => allowedSources.includes(t.id)).map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setFormData({ ...formData, sourceType: t.id as SourceType })}
                    className={`p-5 rounded-2xl border-2 flex items-center gap-4 transition-all ${formData.sourceType === t.id ? 'border-blue-600 bg-blue-50/10 text-blue-600' : 'border-slate-100 dark:border-slate-800 text-slate-400 hover:border-blue-500/30'}`}
                  >
                    <div className={`p-3 rounded-xl ${formData.sourceType === t.id ? 'bg-blue-600 text-white' : 'bg-slate-100 dark:bg-slate-800'}`}>
                      <t.icon size={20} />
                    </div>
                    <span className="text-xs font-black uppercase tracking-widest">{t.label}</span>
                  </button>
                ))}
              </div>
              {errors.sourceType && <p className="text-[10px] text-red-500 font-black uppercase tracking-widest ml-1">{errors.sourceType}</p>}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              <div>
                <h4 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Configuração</h4>
                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-1">
                  {formData.sourceType === 'api' ? 'Sensor em tempo real: a dashboard lê este URL periodicamente' : 'Carregue o ficheiro de dados'}
                </p>
              </div>

              {formData.sourceType === 'api' ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">URL da API *</label>
                    <input
                      type="text"
                      value={formData.apiEndpoint}
                      onChange={(e) => setFormData({ ...formData, apiEndpoint: e.target.value })}
                      className={`w-full p-4 bg-slate-50 dark:bg-slate-900 border ${errors.apiEndpoint ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'} rounded-2xl outline-none focus:ring-2 focus:ring-blue-500/20 font-bold dark:text-white transition-all`}
                      placeholder="http://mqtt-handler:8090/sensors/3CE064F9A8720000/latest"
                    />
                    {errors.apiEndpoint && <p className="text-[10px] text-red-500 font-black uppercase tracking-widest ml-1">{errors.apiEndpoint}</p>}
                    <p className="text-[10px] font-bold text-slate-400 tracking-wide normal-case leading-relaxed">
                      Tem de estar acessível a partir do servidor (auth-service), não do teu browser — é ele que faz a
                      leitura periódica. Com o docker-compose por omissão, isso é o nome do serviço interno,
                      "mqtt-handler".
                    </p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Intervalo de leitura (segundos)</label>
                    <input
                      type="number"
                      min={0.1}
                      step={0.1}
                      value={formData.pollIntervalSec}
                      onChange={(e) => setFormData({ ...formData, pollIntervalSec: Math.max(0.1, parseFloat(e.target.value) || 1) })}
                      className="w-full p-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500/20 font-bold dark:text-white transition-all"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {!formData.file && !isUploading && (
                    <div 
                      onClick={() => !isUploading && fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-[2rem] p-12 text-center cursor-pointer transition-all ${isUploading ? 'border-blue-600 bg-blue-50/10' : 'border-slate-200 dark:border-slate-800 hover:border-blue-500/50 hover:bg-slate-50 dark:hover:bg-slate-900/50'}`}
                    >
                      <div className="w-16 h-16 bg-blue-600/10 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Cloud size={32} />
                      </div>
                      <h5 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-tight">Arraste e solte os ficheiros aqui</h5>
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1 mb-6">ou clique no botão abaixo</p>
                      <button className="px-6 py-3 bg-blue-600 text-white rounded-xl font-black text-[9px] uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20">
                        Carregar Ficheiros
                      </button>
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-4">até 50MB</p>
                    </div>
                  )}

                  {isUploading && (
                    <div className="border-2 border-dashed border-blue-600 bg-blue-50/10 rounded-[2rem] p-12 text-center animate-in fade-in duration-300">
                        <div className="w-16 h-16 bg-blue-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-4 animate-bounce">
                          <Cloud size={32} />
                        </div>
                        <h5 className="text-sm font-black text-blue-600 dark:text-blue-400 uppercase tracking-tight">A Carregar...</h5>
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">A processar o ficheiro {formData.sourceType.toUpperCase()}</p>
                    </div>
                  )}

                  <input ref={fileInputRef} type="file" className="hidden" accept={`.${formData.sourceType}`} onChange={handleFileUpload} />
                  
                  {formData.file && !isUploading && (
                    <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 animate-in fade-in zoom-in-95 duration-200">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-600 text-white rounded-lg"><FileText size={16} /></div>
                        <div>
                          <p className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-tight truncate max-w-[200px]">{formData.file.name}</p>
                          <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">{formData.file.data.length} entradas detectadas</p>
                        </div>
                      </div>
                      <button onClick={() => setFormData({...formData, file: undefined})} className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                  {errors.file && <p className="text-[10px] text-red-500 font-black uppercase tracking-widest ml-1">{errors.file}</p>}
                </div>
              )}
            </div>
          )}

          {step === 3 && formData.file && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              <div>
                <h4 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Selecionar Colunas</h4>
                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-1">Mapeie as colunas do seu ficheiro</p>
              </div>
              
              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Coluna de Tempo (X) *</label>
                  <select
                    value={formData.timeColumn}
                    onChange={(e) => setFormData({ ...formData, timeColumn: e.target.value })}
                    className="w-full p-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500/20 font-bold dark:text-white appearance-none cursor-pointer"
                  >
                    <option value="">Selecione...</option>
                    {formData.file.columns.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {errors.timeColumn && <p className="text-[10px] text-red-500 font-black uppercase tracking-widest ml-1">{errors.timeColumn}</p>}
                </div>

                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Colunas a importar *</label>
                  <div className="grid grid-cols-1 gap-2">
                    {formData.file.columns.filter(c => c !== formData.timeColumn).map(c => (
                      <label key={c} className={`flex items-center justify-between p-4 rounded-2xl border-2 cursor-pointer transition-all ${formData.selectedColumns.includes(c) ? 'border-blue-600 bg-blue-50/10' : 'border-slate-50 dark:border-slate-900 bg-slate-50 dark:bg-slate-900/50 hover:border-blue-500/30'}`}>
                        <span className={`text-[10px] font-black uppercase ${formData.selectedColumns.includes(c) ? 'text-blue-600' : 'text-slate-400'}`}>{c}</span>
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={formData.selectedColumns.includes(c)}
                          onChange={() => {
                            const current = formData.selectedColumns;
                            setFormData({
                              ...formData,
                              selectedColumns: current.includes(c) ? current.filter(i => i !== c) : [...current, c]
                            });
                          }}
                        />
                        <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-all ${formData.selectedColumns.includes(c) ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 dark:border-slate-700'}`}>
                          {formData.selectedColumns.includes(c) && <CheckCircle2 size={12} />}
                        </div>
                      </label>
                    ))}
                  </div>
                  {errors.selectedColumns && <p className="text-[10px] text-red-500 font-black uppercase tracking-widest ml-1">{errors.selectedColumns}</p>}
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              <div>
                <h4 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Pré-visualização</h4>
                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-1">Verifique os dados antes de concluir</p>
              </div>

              <div className="space-y-6">
                {preview.isProcessing ? (
                  <div className="p-8 bg-slate-50 dark:bg-slate-900/50 rounded-[2rem] border border-slate-100 dark:border-slate-800 text-center space-y-4">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest animate-pulse">A gerar pré-visualização...</p>
                    <div className="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-1.5 overflow-hidden">
                      <div className="bg-blue-600 h-full w-1/2 rounded-full animate-[progress_2s_ease-in-out_infinite]" style={{
                        animation: 'progress 2s ease-in-out infinite'
                      }} />
                    </div>
                  </div>
                ) : preview.isComplete ? (
                  <div className="space-y-6 animate-in fade-in duration-500">
                    <div className="p-6 bg-emerald-50 dark:bg-emerald-950/20 rounded-[2rem] border border-emerald-100 dark:border-emerald-900/30 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-emerald-500 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                          <CheckCircle2 size={24} />
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">{formData.sourceType === 'api' ? 'Ligação estabelecida' : 'Processamento Concluído'}</p>
                          <p className="text-xs font-black text-slate-900 dark:text-white uppercase">{formData.file?.name || 'Sensor em tempo real'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                         <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{formData.sourceType === 'api' ? 'Canais detetados' : 'Total de entradas'}</p>
                         <p className="text-xl font-black text-slate-900 dark:text-white">{preview.entryCount}</p>
                      </div>
                    </div>

                    <div className="flex flex-col gap-4">
                      {formData.sourceType === 'api' ? (
                        Object.keys(apiPreviewChannels).length > 0 && (
                          <div className="p-6 bg-slate-50 dark:bg-slate-900/50 rounded-[2rem] border border-slate-100 dark:border-slate-800">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3">Última leitura recebida</p>
                            <div className="flex flex-wrap gap-3">
                              {Object.entries(apiPreviewChannels).map(([k, v]: [string, any]) => (
                                <div key={k} className="px-4 py-2 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                                  <span className="text-[10px] font-black text-slate-400 uppercase">{k}</span>
                                  <span className="ml-2 text-sm font-black text-slate-900 dark:text-white">{Number(v).toFixed(3)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      ) : (
                        <button
                          onClick={() => setShowDataPreview(true)}
                          className="w-full p-6 bg-slate-900 dark:bg-slate-800 text-white rounded-[2rem] flex items-center justify-center gap-3 group hover:bg-black dark:hover:bg-slate-700 transition-all active:scale-[0.98]"
                        >
                          <Eye size={20} className="group-hover:scale-110 transition-transform" />
                          <span className="text-[10px] font-black uppercase tracking-widest">Pré-visualizar gráfico</span>
                        </button>
                      )}

                      {!isAppend && sensorMetadata?.supportedChartTypes?.length > 0 && (
                        <div className="p-6 bg-slate-50 dark:bg-slate-900/50 rounded-[2rem] border border-slate-100 dark:border-slate-800 space-y-4">
                          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Tipo de Gráfico Principal</label>
                          <div className="flex flex-wrap gap-2">
                             {sensorMetadata.supportedChartTypes.map((t: string) => {
                               const type = chartTypeMap[t];
                               return (
                                 <button
                                   key={t}
                                   onClick={() => handleUpdateDefaultChartType(type)}
                                   className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase transition-all ${sensorMetadata.defaultChartType === t ? 'bg-blue-600 text-white shadow-lg' : 'bg-white dark:bg-slate-800 text-slate-400 border border-slate-200 dark:border-slate-700'}`}
                                 >
                                   {t}
                                 </button>
                               );
                             })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : preview.error ? (
                  <div className="p-8 bg-red-50 dark:bg-red-950/20 rounded-[2rem] border border-red-100 dark:border-red-900/30 flex flex-col items-center gap-4 text-center">
                    <div className="w-12 h-12 bg-red-500 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-red-500/20">
                      <Activity size={24} />
                    </div>
                    <p className="text-[10px] font-black text-red-600 dark:text-red-400 uppercase tracking-widest">{preview.error}</p>
                    <button onClick={startProcessing} className="text-[9px] font-black text-slate-400 uppercase tracking-widest hover:text-blue-600 transition-colors flex items-center gap-2">
                       <RefreshCw size={12} /> Tentar novamente
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>

        {/* Footer Buttons */}
        <div className="p-8 bg-slate-50 dark:bg-slate-950/30 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-4">
           {step > 1 ? (
             <button onClick={handleBack} className="flex items-center gap-2 px-8 py-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-400 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:text-slate-600 transition-all">
               <ChevronLeft size={16} /> Voltar
             </button>
           ) : <div />}
           
           <button
             onClick={step === 4 ? handleSubmit : handleNext}
             disabled={isSubmitting || (step === 4 && !preview.isComplete) || isProcessingColumns}
             className="flex items-center gap-2 px-10 py-4 bg-blue-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-blue-700 transition-all shadow-xl shadow-blue-500/20 active:scale-95 disabled:opacity-50"
           >
             {step === 4 ? (isSubmitting ? 'A Guardar...' : 'Concluir') : (
               <>
                 {isProcessingColumns ? 'A Processar...' : 'Continuar'}
                 {!isProcessingColumns && <ChevronRight size={16} />}
               </>
             )}
           </button>
        </div>
        
        {errors.submit && (
           <div className="px-8 pb-4">
             <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 rounded-2xl text-red-600 dark:text-red-400">
                <Activity size={18} />
                <p className="text-[10px] font-black uppercase tracking-widest">{errors.submit}</p>
             </div>
           </div>
        )}
      </div>

      {/* Internal Sub-modal for Data Preview */}
      {showDataPreview && (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center p-4 bg-black/40 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-white dark:bg-[#161b26] rounded-[3.5rem] border border-slate-200 dark:border-slate-800 max-w-4xl w-full p-10 shadow-2xl animate-in zoom-in-95 duration-300 overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between mb-8 shrink-0">
               <div>
                 <h4 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Pré-visualização de Dados</h4>
                 <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-1">Amostra dos dados importados</p>
               </div>
               <button onClick={() => setShowDataPreview(false)} className="p-3 bg-slate-100 dark:bg-slate-800 rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
                  <X size={20} className="text-slate-500" />
               </button>
            </div>
            
            <div className="flex-1 bg-slate-50 dark:bg-slate-950/30 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 p-8 mb-8 overflow-hidden">
               <Chart
                 type={previewChartType as any}
                 height="100%"
                 series={formData.selectedColumns.map(col => ({
                    name: col,
                    data: preview.data.slice(0, 100).map(row => ({
                      x: new Date(row[formData.timeColumn]).getTime(),
                      y: parseFloat(String(row[col]).replace(',', '.'))
                    }))
                 }))}
                 options={{
                    chart: {
                      toolbar: { show: false },
                      background: 'transparent',
                      animations: { enabled: true, easing: 'easeinout', speed: 800 },
                      foreColor: document.documentElement.classList.contains('dark') ? '#94a3b8' : '#64748b',
                    },
                    theme: { mode: document.documentElement.classList.contains('dark') ? 'dark' : 'light' },
                    dataLabels: { enabled: false },
                    stroke: { curve: 'smooth', width: 3 },
                    colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'],
                    grid: { borderColor: document.documentElement.classList.contains('dark') ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.05)', strokeDashArray: 4 },
                    xaxis: { 
                      type: 'datetime',
                      labels: { 
                        style: { colors: document.documentElement.classList.contains('dark') ? '#94a3b8' : '#64748b', fontWeight: 900, fontSize: '9px' },
                        datetimeUTC: false
                      },
                      axisBorder: { show: true, color: document.documentElement.classList.contains('dark') ? '#334155' : '#e2e8f0' },
                      axisTicks: { show: true, color: document.documentElement.classList.contains('dark') ? '#334155' : '#e2e8f0' }
                    },
                    yaxis: { 
                      labels: { style: { colors: document.documentElement.classList.contains('dark') ? '#94a3b8' : '#64748b', fontWeight: 900, fontSize: '9px' } }
                    },
                    tooltip: { theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light', x: { format: 'dd MMM yyyy HH:mm' } },
                    markers: { 
                      size: previewChartType === 'scatter' ? 6 : 4, 
                      strokeWidth: 2, 
                      hover: { size: 8 },
                      colors: undefined,
                      strokeColors: '#fff',
                    },
                    legend: { position: 'top', labels: { colors: document.documentElement.classList.contains('dark') ? '#94a3b8' : '#64748b', useSeriesColors: true } }
                 }}
               />
            </div>

            <div className="flex justify-end shrink-0">
              <button 
                onClick={() => setShowDataPreview(false)}
                className="px-10 py-4 bg-slate-900 dark:bg-slate-800 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-black dark:hover:bg-slate-700 transition-all active:scale-95"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes progress {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
      `}</style>
    </div>
  );
};

export default AddDataResourceModal;
